import React from 'react';
import { ShieldCheck, Users, Lock, Camera } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Container } from '../../components/common';
import { getVisualCmsIcon } from '@/lib/visual-cms/icon-registry';
import { CardDescriptionHtml } from '@/components/visual-cms/CardDescriptionHtml';

interface BadgeItem {
  icon: LucideIcon;
  title: string;
  desc: string;
}

export interface TrustBadgeInput {
  /** Lucide registry key (e.g. "ShieldCheck"). */
  icon?: string | null;
  title: string;
  desc: string;
}

export interface TrustBadgeRowProps {
  badges?: TrustBadgeInput[];
}

const defaultBadges: BadgeItem[] = [
  {
    icon: ShieldCheck,
    title: 'Secure Payments',
    desc: 'Payments are held securely until the rental is complete.',
  },
  {
    icon: Users,
    title: 'Verified Community',
    desc: 'All users are verified to keep our community safe.',
  },
  {
    icon: Lock,
    title: 'PIN Verification',
    desc: 'A secure PIN helps confirm the right person is completing the handover.',
  },
  {
    icon: Camera,
    title: 'Condition Verified',
    desc: 'Photos at pickup and return help record the item\'s condition.',
  },
];

export const TrustBadgeRow: React.FC<TrustBadgeRowProps> = ({ badges: cmsBadges }) => {
  const badges: BadgeItem[] =
    cmsBadges && cmsBadges.length > 0
      ? cmsBadges.map((b, i) => ({
          icon: getVisualCmsIcon(b.icon ?? null) ?? defaultBadges[i % defaultBadges.length].icon,
          title: b.title || defaultBadges[i % defaultBadges.length].title,
          desc: b.desc || defaultBadges[i % defaultBadges.length].desc,
        }))
      : defaultBadges;

  return (
    <section className="-mt-6 lg:-mt-14 relative z-10">
      <Container>
        <div className="bg-v2-neutral-0 rounded-v2-lg border border-v2-neutral-200 shadow-sm grid grid-cols-1 py-5 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-v2-neutral-200 overflow-hidden">
          {badges.map(({ icon: BadgeIcon, title, desc }) => (
            <div key={title} className="flex items-start gap-4 px-6 py-2">
              <div className="w-14 h-14 rounded-v2-full bg-v2-green-50 flex items-center justify-center text-v2-green-500 shrink-0">
                <BadgeIcon size={40} strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold font-display text-[14px] text-v2-neutral-900 mb-0.5">{title}</p>
                <CardDescriptionHtml as="p" text={desc} className="text-[12px] text-v2-neutral-500 leading-snug" />
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};
